import React from 'react';
import {TouchableOpacity, View} from 'react-native';
import {useNavigation} from '@react-navigation/native';

import Text from '/component/base/Text';
import VolumePerformanceTestIDs from '/constant/TestIDs/VolumePerformance';
import {CLIENT_PROFILE, COACH_PROFILE} from '/enum/popover.enum';
import {ORDER_DETAILS, ORDER_HISTORY_LIST_SCREEN} from '/enum/records.enum';
import {getDateUtilDefaultInstance} from '/util/date.util';

import {
  sanitize,
  USER_CARD,
} from '../constants/volumePerformanceReport.constants';
import {styles} from '../styles/VolPerUserCard.style';
import {VolPerUserCardProps} from '../types/VolPerfComponent.types';
const commonTextProps = {
  isBold: true,
  numberOfLines: 1,
  adjustsFontSizeToFit: true,
  minimumFontScale: 0.75,
  accessible: false,
};

export const VolPerUserCard: React.FC<VolPerUserCardProps> = ({
  orderId,
  firstName,
  lastName,
  customerTypeId,
  orderType,
  orderStatus,
  creationDate,
  nextOrderDate,
  lastOrderDate,
  orderNumber,
  referenceNumber,
  isScheduledResult = false,
  qv,
  consumableSubTotal,
  totalPrice,
}) => {
  const navigation: any = useNavigation();
  const name = `${firstName} ${lastName}`;
  const role =
    customerTypeId === 1 ? USER_CARD.ROLES.COACH : USER_CARD.ROLES.CLIENT;
  const dateUtil = getDateUtilDefaultInstance();
  const normalizedOrderType = orderType?.trim().toLowerCase();
  const isPremierPlusOrderType = normalizedOrderType === 'premier+';
  const isOnDemandOrderType = [
    'ondemad',
    'ondemand',
    'on-demand',
    'on demand',
  ].includes(normalizedOrderType || '');
  const displayOrderType = isPremierPlusOrderType
    ? USER_CARD.ORDER_TYPES.PREMIER_PLUS
    : isOnDemandOrderType
    ? USER_CARD.ORDER_TYPES.ON_DEMAND
    : orderType;

  // Normalize status for badge styles
  const normalizedStatus =
    orderStatus.charAt(0).toUpperCase() + orderStatus.slice(1);

  const formatCurrency = (value: number | string) => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) {
      return value;
    }
    const isNegative = numValue < 0;
    const absValue = Math.abs(numValue).toFixed(2);
    const result = `$${absValue}`;
    return isNegative ? `(-${result})` : result;
  };

  const formatQV = (value: number | string) => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) {
      return value;
    }
    const isNegative = numValue < 0;
    return isNegative ? `(${numValue})` : numValue.toString();
  };

  const getBadgeStyles = () => {
    switch (normalizedStatus) {
      case USER_CARD.STATUS.RETURNED:
        return {
          container: styles.badgeReturned,
          text: styles.badgeTextReturned,
        };
      case USER_CARD.STATUS.SCHEDULED:
        return {
          container: styles.badgeScheduled,
          text: styles.badgeTextScheduled,
        };
      case USER_CARD.STATUS.CANCELLED:
        return {
          container: styles.badgeCancelled,
          text: styles.badgeTextCancelled,
        };
      case USER_CARD.STATUS.ORDER:
      case USER_CARD.STATUS.SHIPPED:
      default:
        return {container: styles.badgeOrder, text: styles.badgeTextOrder};
    }
  };

  const badgeStyle = getBadgeStyles();

  const getOrderIdPrefix = () => {
    switch (normalizedStatus) {
      case USER_CARD.STATUS.RETURNED:
        return USER_CARD.ORDER_ID_PREFIX.RET;
      case USER_CARD.STATUS.SCHEDULED:
        return USER_CARD.ORDER_ID_PREFIX.SCH;
      case USER_CARD.STATUS.CANCELLED:
        return USER_CARD.ORDER_ID_PREFIX.CANC;
      default:
        return USER_CARD.ORDER_ID_PREFIX.ORD;
    }
  };

  const displayOrderNumber =
    referenceNumber || `${getOrderIdPrefix()}${orderNumber}`;

  const formattedSubTotal =
    consumableSubTotal && formatCurrency(consumableSubTotal);
  const formattedTotalPrice = formatCurrency(totalPrice);
  const formattedQV = formatQV(qv);

  let label1: string = USER_CARD.LABELS.ORDER_DATE;
  let date1 = creationDate;
  let label2 = '';
  let date2 = '';
  let label3 = '';
  let date3 = '';

  const isCancelledScheduledResult =
    normalizedStatus === USER_CARD.STATUS.CANCELLED && isScheduledResult;

  switch (normalizedStatus) {
    case USER_CARD.STATUS.SCHEDULED:
      label1 = USER_CARD.LABELS.SCHEDULED_FOR;
      date1 = nextOrderDate || '';
      label2 = USER_CARD.LABELS.LAST_PREMIER_ORDER;
      date2 = lastOrderDate || '';
      break;
    case USER_CARD.STATUS.CANCELLED:
      if (isCancelledScheduledResult) {
        // For scheduled cancelled cards, keep reference number on the same row
        // as the cancelled scheduled date.
        label1 = USER_CARD.LABELS.CANCELLED_SCHEDULED;
        date1 = lastOrderDate || '';
        label2 = USER_CARD.LABELS.NEXT_PREMIER_ORDER;
        date2 = nextOrderDate || '';
        label3 = '';
        date3 = '';
        break;
      }
      label1 =
        isPremierPlusOrderType || isOnDemandOrderType
          ? USER_CARD.LABELS.CANCEL_DATE
          : USER_CARD.LABELS.SCHEDULED_FOR;
      date1 = creationDate;
      label2 = isPremierPlusOrderType
        ? USER_CARD.LABELS.NEXT_PREMIER_ORDER
        : isOnDemandOrderType
        ? ''
        : USER_CARD.LABELS.LAST_PREMIER_ORDER;
      date2 = isPremierPlusOrderType
        ? nextOrderDate || ''
        : isOnDemandOrderType
        ? ''
        : lastOrderDate || '';
      label3 = '';
      date3 = '';
      break;
    case USER_CARD.STATUS.RETURNED:
      label1 = USER_CARD.LABELS.RETURN_DATE;
      date1 = creationDate;
      label2 = '';
      date2 = '';
      label3 = '';
      date3 = '';
      break;
    case USER_CARD.STATUS.ORDER:
    case USER_CARD.STATUS.SHIPPED:
    default:
      label1 = USER_CARD.LABELS.ORDER_DATE;
      date1 = creationDate;
      label2 = USER_CARD.LABELS.NEXT_PREMIER_ORDER;
      date2 = nextOrderDate || '';
      label3 = '';
      date3 = '';
      break;
  }

  const formattedDate1 = date1 ? dateUtil.formatDate(date1, 'MMM d, yyyy') : '';
  const formattedDate2 = date2 ? dateUtil.formatDate(date2, 'MMM d, yyyy') : '';
  const formattedDate3 = date3 ? dateUtil.formatDate(date3, 'MMM d, yyyy') : '';
  const displayValue1 = formattedDate1;
  const displayValue2 =
    label2 === USER_CARD.LABELS.NEXT_PREMIER_ORDER && isScheduledResult
      ? formattedDate2 || 'Never Ordered'
      : formattedDate2;
  const displayValue3 =
    label3 === USER_CARD.LABELS.NEXT_PREMIER_ORDER && isScheduledResult
      ? formattedDate3 || 'Never Ordered'
      : formattedDate3;
  const isScheduledOrCancelled =
    normalizedStatus === USER_CARD.STATUS.SCHEDULED ||
    normalizedStatus === USER_CARD.STATUS.CANCELLED;

  const isClickable = normalizedStatus === USER_CARD.STATUS.ORDER;

  // const onHandlePress = () => {
  //   if (isClickable) {
  //     navigation.push('card', {
  //       modalName: ORDER_DETAILS,
  //       modalProps: {
  //         orderId,
  //       },
  //       orderId,
  //     });
  //     // navigation.push('client-profile-order-detail-screen', {orderId});

  //   }
  // };

  const onHandlePress = () => {
    if (isClickable) {
      const onViewProfile = ({clientId, coachId}: any = {}) => {
        if (clientId) {
          navigation.push('card', {
            modalName: CLIENT_PROFILE,
            modalProps: {recordId: clientId},
          });
        } else if (coachId) {
          navigation.push('card', {
            modalName: COACH_PROFILE,
            modalProps: {recordId: coachId},
          });
        }
      };
      const onViewOrderHistory = ({clientId, coachId}: any = {}) => {
        const profileId = clientId || coachId;
        if (!profileId) {
          return;
        }

        navigation.push('card', {
          modalName: ORDER_HISTORY_LIST_SCREEN,
          modalProps: {
            clientId: profileId,
            fetchOnMount: true,
            onBackPress: () => navigation.goBack(),
            onOrderPress: ({orderId: selectedOrderId}: any = {}) => {
              if (!selectedOrderId) {
                return;
              }

              navigation.push('card', {
                modalName: ORDER_DETAILS,
                modalProps: {
                  orderId: selectedOrderId,
                  onViewProfile,
                  onViewOrderHistory,
                },
              });
            },
          },
        });
      };

      const modalProps = {
        orderId,
        onViewProfile,
        onViewOrderHistory,
      };

      navigation.push('card', {
        modalName: ORDER_DETAILS,
        modalProps,
      });
    }
  };

  return (
    <TouchableOpacity
      activeOpacity={0.7}
      onPress={onHandlePress}
      disabled={!isClickable}
      style={styles.card}>
      <View style={styles.container}>
        {/* Header Section */}
        <View style={styles.header}>
          <View
            style={styles.userInfo}
            accessible={true}
            accessibilityLabel={`User: ${name}, Role: ${role}`}
            testID={VolumePerformanceTestIDs.volPerfUserInfo}>
            <Text style={styles.userName} {...commonTextProps}>
              {name}
            </Text>
            <Text style={styles.userRole} {...commonTextProps}>
              {role}
            </Text>
          </View>
          <View
            style={styles.headerRight}
            testID={VolumePerformanceTestIDs.volPerfOrderStatusContainer}
            accessible={true}
            accessibilityLabel={`Order Type: ${displayOrderType}, Status: ${normalizedStatus}`}>
            <Text
              style={styles.orderType}
              {...commonTextProps}
              testID={`${VolumePerformanceTestIDs.volPerfOrderType}-${sanitize(
                displayOrderType,
              )}`}>
              {displayOrderType}
            </Text>
            <View
              style={[styles.badge, badgeStyle.container]}
              accessible={false}>
              <Text
                style={[styles.badgeText, badgeStyle.text]}
                {...commonTextProps}
                testID={`${
                  VolumePerformanceTestIDs.volPerfOrderStatus
                }-${sanitize(normalizedStatus)}`}>
                {normalizedStatus}
              </Text>
            </View>
          </View>
        </View>

        {/* Order Details Section */}
        <View
          style={styles.detailsRow}
          testID={VolumePerformanceTestIDs.volPerfOrderDetails}
          accessible={true}
          accessibilityLabel={`Order Details: ${label1} ${displayValue1}${
            displayValue2 ? `, ${label2} ${displayValue2}` : ''
          }${
            displayValue3 ? `, ${label3} ${displayValue3}` : ''
          }, Order Number : ${displayOrderNumber}`}>
          <View style={styles.dateAndOrderRow}>
            {displayValue1 ? (
              <Text
                style={[
                  styles.detailText,
                  styles.primaryDateText,
                  isScheduledOrCancelled && styles.detailTextScheduledCard,
                ]}
                {...commonTextProps}
                numberOfLines={1}
                adjustsFontSizeToFit={true}
                minimumFontScale={0.65}
                testID={`vol-perf-${sanitize(label1)}`}>
                {label1}: {displayValue1}
              </Text>
            ) : null}
            <Text
              style={[
                styles.detailText,
                styles.orderNumber,
                isScheduledOrCancelled && styles.detailTextScheduledCard,
                isScheduledOrCancelled && styles.orderNumberScheduledCard,
              ]}
              {...commonTextProps}
              numberOfLines={1}
              adjustsFontSizeToFit={true}
              minimumFontScale={0.65}
              testID={`vol-perf-order-id-${sanitize(displayOrderNumber)}`}>
              {displayOrderNumber}
            </Text>
          </View>
          {displayValue2 ? (
            <Text
              style={[
                styles.detailText,
                isScheduledOrCancelled && styles.detailTextScheduledCard,
              ]}
              {...commonTextProps}
              numberOfLines={1}
              adjustsFontSizeToFit={true}
              minimumFontScale={0.65}
              testID={`vol-perf-${sanitize(label2)}`}>
              {label2}: {displayValue2}
            </Text>
          ) : null}
          {displayValue3 ? (
            <Text
              style={[
                styles.detailText,
                isScheduledOrCancelled && styles.detailTextScheduledCard,
              ]}
              {...commonTextProps}
              numberOfLines={1}
              adjustsFontSizeToFit={true}
              minimumFontScale={0.65}
              testID={`vol-perf-${sanitize(label3)}`}>
              {label3}: {displayValue3}
            </Text>
          ) : null}
        </View>

        {/* Stats and Totals Section */}
        <View style={styles.statsContainer}>
          <View
            style={styles.statItem}
            testID={VolumePerformanceTestIDs.volPerfQVContainer}
            accessible={true}
            accessibilityLabel={`QV: ${formattedQV}`}>
            <Text
              style={styles.statLabel}
              {...commonTextProps}
              testID={VolumePerformanceTestIDs.volPerfQVLabel}>
              {USER_CARD.LABELS.QV}
            </Text>
            <Text
              style={styles.statValue}
              {...commonTextProps}
              testID={VolumePerformanceTestIDs.volPerfQVValue}>
              {formattedQV}
            </Text>
          </View>
          {consumableSubTotal != null && consumableSubTotal !== '' && (
            <View
              style={styles.subTotalContainer}
              testID={VolumePerformanceTestIDs.volPerfSubtotalContainer}
              accessible={true}
              accessibilityLabel={`Sub-total: ${formattedSubTotal}`}>
              <Text
                style={styles.subTotalLabel}
                {...commonTextProps}
                testID={VolumePerformanceTestIDs.volPerfSubtotalLabel}>
                {USER_CARD.LABELS.SUB_TOTAL}
              </Text>
              <Text
                style={styles.statValue}
                {...commonTextProps}
                testID={VolumePerformanceTestIDs.volPerfSubtotalValue}>
                {formattedSubTotal}
              </Text>
            </View>
          )}
          <View
            style={styles.totalContainer}
            testID={VolumePerformanceTestIDs.volPerfTotalContainer}
            accessible={true}
            accessibilityLabel={`Total Amount: ${formattedTotalPrice}`}>
            <Text
              style={styles.totalLabel}
              {...commonTextProps}
              testID={VolumePerformanceTestIDs.volPerfTotalLabel}>
              {USER_CARD.LABELS.TOTAL}
            </Text>
            <Text
              style={styles.totalValue}
              {...commonTextProps}
              testID={VolumePerformanceTestIDs.volPerfTotalValue}>
              {formattedTotalPrice}
            </Text>
          </View>
        </View>
      </View>
    </TouchableOpacity>
  );
};

export default VolPerUserCard;
